import React from "react";
import { ChevronUpIcon } from "@heroicons/react/24/outline";

const Footer = () => {
  const currentYear = new Date().getFullYear();

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) element.scrollIntoView({ behavior: "smooth" });
  };

  const quickLinks = ["home", "about", "services", "lessons"];
  const services = [
    "One-on-One Tutoring",
    "Group Classes",
    "IELTS Preparation",
    "Business English",
  ];

  const socialLinks = [
    {
      name: "Facebook",
      icon: "/images/fb-thumb.png",
      color: "hover:bg-blue-600",
    },
    {
      name: "GitHub",
      icon: "/images/github-thumb.png",
      color: "hover:bg-gray-700",
    },
    {
      name: "YouTube",
      icon: "/images/youtube-thumb.png",
      color: "hover:bg-red-600",
    },
    {
      name: "Instagram",
      icon: "/images/instagram-thumb.png",
      color: "hover:bg-pink-600",
    },
  ];

  const contactInfo = [
    "📧 rashida@englishjanala.com",
    "📱 +880 1234-567890",
    "📍 Dhaka, Bangladesh",
    "🕒 Mon-Sat: 9AM-8PM",
  ];

  return (
    <footer className="bg-gradient-to-br from-gray-900 via-gray-800 to-indigo-900 dark:from-black dark:via-gray-900 dark:to-purple-900 text-white relative overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 bg-gradient-to-r from-indigo-600/10 to-purple-600/10 dark:from-blue-600/10 dark:to-cyan-600/10"></div>
      <div className="absolute inset-0 opacity-20">
        <div
          className="w-full h-full bg-repeat"
          style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%239C92AC' fill-opacity='0.05'%3E%3Ccircle cx='30' cy='30' r='2'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          }}
        ></div>
      </div>

      <div className="relative max-w-7xl mx-auto px-4 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Brand Section */}
          <div className="space-y-6" data-aos="fade-up">
            <div className="flex items-center space-x-3">
              <div className="w-12 h-12 bg-gradient-to-r from-indigo-500 to-purple-600 dark:from-blue-500 dark:to-cyan-500 rounded-xl flex items-center justify-center shadow-lg">
                <img
                  src="/images/logo.png"
                  alt="English Janala Logo"
                  className="w-8 h-8"
                />
              </div>
              <div className="flex items-center space-x-1">
                <span className="text-xl font-bold font-display">English</span>
                <span className="text-xl font-bold font-bangla bg-gradient-to-r from-indigo-400 to-purple-400 dark:from-blue-400 dark:to-cyan-400 bg-clip-text text-transparent">
                  জানালা
                </span>
              </div>
            </div>
            <p className="text-gray-300 dark:text-gray-400 leading-relaxed">
              Empowering students to achieve English fluency through
              personalized, interactive, and effective teaching methods.
            </p>
            <div className="flex space-x-4">
              {socialLinks.map((social, index) => (
                <a
                  key={index}
                  href="#"
                  className={`w-10 h-10 bg-gray-800/50 dark:bg-gray-700/50 backdrop-blur-sm rounded-full flex items-center justify-center ${social.color} dark:hover:bg-gray-600 transition-all transform hover:scale-110 hover:shadow-lg border border-gray-700/50 dark:border-gray-600/50`}
                  title={social.name}
                  data-aos="zoom-in"
                  data-aos-delay={200 + index * 100}
                >
                  <img
                    src={social.icon}
                    alt={social.name}
                    className="w-5 h-5"
                  />
                </a>
              ))}
            </div>
          </div>

          {/* Quick Links */}
          <div data-aos="fade-up" data-aos-delay="200">
            <h4 className="text-lg font-bold font-display mb-6 bg-gradient-to-r from-indigo-400 to-purple-400 dark:from-blue-400 dark:to-cyan-400 bg-clip-text text-transparent">
              Quick Links
            </h4>
            <ul className="space-y-3">
              {quickLinks.map((section, index) => (
                <li
                  key={section}
                  data-aos="fade-right"
                  data-aos-delay={400 + index * 100}
                >
                  <button
                    onClick={() => scrollToSection(section)}
                    className="text-gray-300 dark:text-gray-400 hover:text-white dark:hover:text-blue-400 transition-colors capitalize hover:translate-x-1 transform duration-200"
                  >
                    {section}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* Services */}
          <div data-aos="fade-up" data-aos-delay="400">
            <h4 className="text-lg font-bold font-display mb-6 bg-gradient-to-r from-indigo-400 to-purple-400 dark:from-blue-400 dark:to-cyan-400 bg-clip-text text-transparent">
              Services
            </h4>
            <ul className="space-y-3">
              {services.map((service, index) => (
                <li
                  key={index}
                  data-aos="fade-right"
                  data-aos-delay={600 + index * 100}
                >
                  <a
                    href="#"
                    className="text-gray-300 dark:text-gray-400 hover:text-white dark:hover:text-blue-400 transition-colors hover:translate-x-1 transform duration-200"
                  >
                    {service}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div data-aos="fade-up" data-aos-delay="600">
            <h4 className="text-lg font-bold font-display mb-6 bg-gradient-to-r from-indigo-400 to-purple-400 dark:from-blue-400 dark:to-cyan-400 bg-clip-text text-transparent">
              Contact
            </h4>
            <div className="space-y-3">
              {contactInfo.map((contact, index) => (
                <p
                  key={index}
                  className="text-gray-300 dark:text-gray-400"
                  data-aos="fade-left"
                  data-aos-delay={800 + index * 100}
                >
                  {contact}
                </p>
              ))}
            </div>
          </div>
        </div>

        {/* Bottom Section */}
        <div
          className="border-t border-gray-700/50 dark:border-gray-600/50 mt-12 pt-8"
          data-aos="fade-up"
          data-aos-delay="1000"
        >
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            <p className="text-gray-300 dark:text-gray-400">
              © {currentYear} English Janala. All rights reserved.
            </p>
            <div className="flex items-center space-x-6">
              <a
                href="#"
                className="text-gray-300 dark:text-gray-400 hover:text-white dark:hover:text-blue-400 transition-colors"
                data-aos="fade-up"
                data-aos-delay="1200"
              >
                Privacy Policy
              </a>
              <a
                href="#"
                className="text-gray-300 dark:text-gray-400 hover:text-white dark:hover:text-blue-400 transition-colors"
                data-aos="fade-up"
                data-aos-delay="1300"
              >
                Terms of Service
              </a>
              <button
                onClick={scrollToTop}
                className="bg-gradient-to-r from-indigo-500 to-purple-600 dark:from-blue-500 dark:to-cyan-500 text-white p-2 rounded-full hover:from-indigo-600 hover:to-purple-700 dark:hover:from-blue-600 dark:hover:to-cyan-600 transition-all transform hover:scale-110 shadow-lg hover:shadow-xl"
                title="Back to top"
                data-aos="zoom-in"
                data-aos-delay="1400"
              >
                <ChevronUpIcon className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
